import { useEffect, useState } from 'react';

import { CaseJourneysResponse, JourneyGroup, getCaseJourneys } from '@brands/services/cases/multiStage';
import { ICase } from '@brands/services/cases/types/ICase';

export type CaseJourneyMatch = { journey: JourneyGroup; patientId: number };

export type CaseJourneyLookup = {
  /** Case id -> the journey it belongs to. */
  byCase: Map<number, CaseJourneyMatch>;
  /** Every journey found, once each — a journey whose cases are all filtered
   *  out of the appointment list still has to be shown somewhere. */
  journeys: CaseJourneyMatch[];
};

const EMPTY_LOOKUP: CaseJourneyLookup = { byCase: new Map(), journeys: [] };

// Where a journey is listed depends on its status alone. A paused journey
// hasn't ended, so it stays with the running ones on the dashboard and under
// Upcoming Appointments; only a completed or canceled journey is care history.
export const isOpenJourney = (status: string): boolean => status === 'in_progress' || status === 'paused';
export const isClosedJourney = (status: string): boolean => status === 'complete' || status === 'canceled';

/**
 * Maps each case id to the multi-stage journey it belongs to, so an appointment
 * card can render its own journey strip. Accepts several patient ids because
 * the dashboard shows the account holder's cases alongside their dependents'.
 *
 * A journey of one case is not a journey yet — the same rule the standalone
 * strip uses — so single-case groups are excluded.
 */
export const useCaseJourneyLookup = (patientIds: number[], enabled = true): CaseJourneyLookup => {
  // Tagged with the ids it was fetched for. Switching patients changes the key
  // immediately while the replacement request is still in flight, and callers
  // relabel their strips the moment they switch — so anything still carrying
  // the previous key must read as empty rather than show the previous
  // patient's journeys under the newly selected patient's name.
  const [lookup, setLookup] = useState<{ key: string; value: CaseJourneyLookup }>({ key: '', value: EMPTY_LOOKUP });
  // Depend on the ids by value: a fresh array each render would otherwise
  // re-fetch on every render.
  const key = patientIds.filter(Boolean).join(',');

  useEffect(() => {
    if (!enabled || !key) {
      setLookup({ key, value: EMPTY_LOOKUP });
      return undefined;
    }
    let active = true;
    const ids = key.split(',').map(Number);
    Promise.all(
      ids.map((id) =>
        getCaseJourneys(id)
          .then((response: CaseJourneysResponse) => ({ id, response }))
          // Journeys are supplementary to the appointment list: a failed lookup
          // must leave the appointments rendered, just without their strip.
          .catch(() => null),
      ),
    ).then((results) => {
      if (!active) return;
      const byCase = new Map<number, CaseJourneyMatch>();
      const journeys: CaseJourneyMatch[] = [];
      results.forEach((result) => {
        if (!result) return;
        result.response.multi_stage
          .filter((journey) => journey.cases.length > 1)
          .forEach((journey) => {
            const match = { journey, patientId: result.id };
            journeys.push(match);
            journey.cases.forEach((journeyCase) => {
              byCase.set(journeyCase.id, match);
            });
          });
      });
      setLookup({ key, value: { byCase, journeys } });
    });
    return () => {
      active = false;
    };
  }, [key, enabled]);

  // A shared constant, not a fresh object: callers memoise on this value, and a
  // new identity each render would recompute their anchors every time.
  return lookup.key === key ? lookup.value : EMPTY_LOOKUP;
};

/** Keeps only the earliest listable case of each open journey; the rest are reachable through the journey link. */
export const collapseJourneyCases = (list: ICase[], lookup: CaseJourneyLookup, listedStatuses: string[]): ICase[] => {
  // From the journey's own cases, not the list: the list is paginated, so an older case can be a page away.
  // Statuses come from the caller's query, so the case picked is one that query can actually return.
  const keep = new Map<number, number>();
  lookup.journeys.forEach(({ journey }) => {
    if (!isOpenJourney(journey.status)) return;
    const listable = journey.cases.filter((journeyCase) => listedStatuses.includes(journeyCase.status));
    if (listable.length === 0) return;
    const earliest = listable.reduce((a, b) => (new Date(b.created_at) < new Date(a.created_at) ? b : a));
    keep.set(journey.case_journey_id, earliest.id);
  });
  return list.filter((caseItem) => {
    const match = lookup.byCase.get(Number(caseItem.id));
    if (!match || !isOpenJourney(match.journey.status)) return true;
    return keep.get(match.journey.case_journey_id) === Number(caseItem.id);
  });
};
